import { cloneDeep } from 'lodash';

import { getOnline } from 'State/selectors';
import { TransactionTypeEnum } from 'Common/Constants/Enumeration/TransactionTypeEnum';
import {
  isGenericAccount,
  getChargeLimitNotification,
} from 'PointOfSale/modules/common/helpers';
import { getProfile } from 'PointOfSale/modules/individualServing/state/selectors';
import { NOTIFICATION_DIALOG_MESSAGES } from 'Common/Constants';
import { getPreferences } from 'Common/Security/state/selectors';
import {
  getTax,
  getCost,
  getTaxRate,
  getBalance,
  getSubtotal,
  getAmountPaid,
  hasAmountPaid,
  getBoughtItems,
  hasBoughtItems,
  hasCheckNumber,
  hasNegativeAmountPaid,
  isValidTransaction,
  hasMultipleReimbursableMeals,
} from 'PointOfSale/helpers/transactions';
import { isPreferenceEnabled } from 'Common/Security/SecurityHelpers';
import { PreferencesEnum } from 'Common/Security/PreferencesEnum';
import {
  setYesNoDialogProps,
  setNotificationDialog,
} from 'Components/dialogs/state/actions';
import { createToast, toastType } from 'Common/ToastHelper';
import {
  isValidProfile,
  canChargeAccount,
  getChargeStatus,
} from 'PointOfSale/helpers/profile';
import { getTaxRatesByStatus } from './selectors';

const validateProfile = (transaction, dispatch, state, { profile }) => {
  profile = profile || getProfile(state);
  if (!isValidProfile(profile)) {
    return {
      transaction,
      error: {
        type: 'toast',
        title: 'Error',
        message:
          'A valid account needs to be selected to process a transaction',
      },
    };
  }

  return { transaction };
};

const validateTransactionObject = (transaction, dispatch, state) => {
  const profile = getProfile(state);
  const balance = getBalance(profile);
  const amountPaid = getAmountPaid(transaction);
  const isOnline = getOnline(state);

  if (
    amountPaid < 0 &&
    transaction.TransactionType !== TransactionTypeEnum.Charge
  ) {
    return {
      transaction,
      error: {
        type: 'toast',
        title: 'Error',
        message: 'For refunds, please use the Account button.',
      },
    };
  }
  if (
    amountPaid < 0 &&
    transaction.TransactionType === TransactionTypeEnum.Charge &&
    balance < 0 &&
    isOnline
  ) {
    return {
      transaction,
      error: {
        type: 'toast',
        title: 'Error',
        message:
          'Transactions that include refunds cannot exceed the balance of the account.',
      },
    };
  }

  if (
    amountPaid < 0 &&
    transaction.TransactionType === TransactionTypeEnum.Charge &&
    !isOnline
  ) {
    return {
      transaction,
      error: {
        type: 'toast',
        title: 'Error',
        message: 'Refunds cannot be processed in Offline Mode.',
      },
    };
  }

  if (!isValidTransaction(transaction)) {
    return {
      transaction,
      error: {
        type: 'toast',
        ...NOTIFICATION_DIALOG_MESSAGES.TRANSACTION_GENERIC_ERROR,
      },
    };
  }

  return { transaction };
};

const validateNotGenericAccount = (
  transaction,
  dispatch,
  state,
  { profile }
) => {
  profile = profile || getProfile(state);

  if (isGenericAccount(profile)) {
    return {
      transaction,
      error: {
        type: 'toast',
        title: 'Error',
        message:
          'Only Cash and Check transactions are allowed for generic accounts.',
      },
    };
  }

  return { transaction };
};

const validateAmountPaid = (
  transaction,
  dispatch,
  state,
  {
    subtotal = getSubtotal(getBoughtItems(transaction)),
    tax = getTax(
      subtotal,
      getTaxRate(getProfile(state), getTaxRatesByStatus(state))
    ),
    total = getCost(subtotal, tax),
    amountPaid = getAmountPaid(transaction),
  }
) => {
  /** Student has given exact cash, set the AmountPaid to total */
  if (amountPaid < total && amountPaid !== 0) {
    return {
      transaction,
      error: {
        type: 'dialog',
        title: 'Attention',
        message: 'For Payments less than Total Due please use Account button.',
      },
    };
  }

  // Set amountPaid to the total since when the cash button is press the difference will be given as change.
  transaction.AmountPaid = total;

  return { transaction };
};

const validateCanChargeAccount = (
  transaction,
  dispatch,
  state,
  {
    profile = getProfile(state),
    amountPaid = getAmountPaid(transaction),
    subtotal = getSubtotal(getBoughtItems(transaction)),
    tax = getTax(
      subtotal,
      getTaxRate(getProfile(state), getTaxRatesByStatus(state))
    ),
    total = getCost(subtotal, tax),
    balance = getBalance(profile),
    chargeStatus = getChargeStatus(profile),
  }
) => {
  //skip this validation if there is no charge status.
  if (!chargeStatus) {
    return { transaction };
  }
  //skip if transaction is a valid refund
  if (amountPaid < 0 && balance > 0) {
    return { transaction };
  }

  //skip if the total is $0 because it doesn't count as a charge
  if (total === 0) {
    return { transaction };
  }

  if (amountPaid + balance < total && !canChargeAccount(profile)) {
    return {
      transaction,
      error: {
        type: 'dialog',
        title: 'Attention',
        message: 'Account does not allow charge.',
      },
    };
  }

  return { transaction };
};

const validateHasBoughtItems = (transaction) => {
  if (!hasBoughtItems(transaction)) {
    return {
      transaction,
      error: {
        type: 'toast',
        title: 'Error',
        message: 'Please select an item.',
      },
    };
  }

  return { transaction };
};

const validateAmountPaidOrItemSelected = (transaction) => {
  if (
    hasAmountPaid(transaction) ||
    hasBoughtItems(transaction) ||
    (hasNegativeAmountPaid(transaction) &&
      transaction.TransactionType === TransactionTypeEnum.Charge)
  ) {
    return { transaction };
  }

  return {
    transaction,
    error: {
      type: 'toast',
      title: 'Error',
      message: 'Please enter an amount paid or select an item.',
    },
  };
};

const validateIsCheckNumber = (transaction) => {
  if (!hasCheckNumber(transaction)) {
    return {
      transaction,
      error: {
        type: 'toast',
        title: 'Error',
        message: 'Please enter check number.',
      },
    };
  }

  return { transaction };
};

const validateIsCheckAmount = (transaction) => {
  if (!getAmountPaid(transaction)) {
    return {
      transaction,
      error: {
        type: 'toast',
        title: 'Error',
        message: 'Please enter check amount.',
      },
    };
  }

  return { transaction };
};

const validateCheckAmountOrAmountPaid = (transaction) => {
  if (hasCheckNumber(transaction) && !hasAmountPaid(transaction)) {
    return {
      transaction,
      error: {
        type: 'toast',
        title: 'Error',
        message: 'Please enter check amount or an amount paid.',
      },
    };
  }

  return { transaction };
};

const validateIsChargedMealLimitReached = async (
  transaction,
  dispatch,
  state,
  {
    profile = getProfile(state),
    preferences = getPreferences(state),
    amountPaid = getAmountPaid(transaction),
  }
) => {
  const error = getChargeLimitNotification({
    profile,
    pendingItems: transaction.BoughtItems,
    amountPaid,
  });

  if (error) {
    const canOverride = isPreferenceEnabled(
      PreferencesEnum.AllowOverrideChargeLimits,
      preferences
    );

    let shouldOverride = false;

    if (canOverride) {
      shouldOverride = await new Promise((resolve) => {
        dispatch(
          setYesNoDialogProps({
            showDialog: true,
            title: `${error.title} - ${profile.FullName}`,
            question:
              error.message +
              ' Would you like to override the limit and continue?',
            yesCallback: () => resolve(true),
            noCallback: () => resolve(false),
          })
        );
      });
    }

    // If the user chooses to override this warning don't return any error, otherwise return an error without a type.
    if (!shouldOverride) {
      //the validator needs to know about the error regardless of whether we've
      //already seen it or not. however, it will only show the dialog box if we
      //haven't seen it before
      if (!canOverride) {
        error.type = 'dialog';
      }

      //turns out that even if I make changes to the transaction I return, those
      //changes don't have any effect if the transaction errors out. not a big
      //deal, but it means I can't remove the bad item from here

      return {
        transaction,
        error,
      };
    }
  }

  return { transaction };
};

const validateGenericAccountPaidByCheck = (transaction, dispatch, state) => {
  const profile = getProfile(state);
  const amountPaid = getAmountPaid(transaction);
  const subtotal = getSubtotal(getBoughtItems(transaction));
  const tax = getTax(
    subtotal,
    getTaxRate(getProfile(state), getTaxRatesByStatus(state))
  );
  const total = getCost(subtotal, tax);
  if (isGenericAccount(profile) && total !== amountPaid) {
    return {
      transaction,
      error: {
        type: 'toast',
        title: 'Error',
        message: 'Check amount must match amount due for generic accounts.',
      },
    };
  }

  return { transaction };
};

const validateOnlyCheckPaymentsCanIncludeCheckNumber = (transaction) => {
  if (hasCheckNumber(transaction)) {
    return {
      transaction,
      error: {
        type: 'toast',
        title: 'Error',
        message: 'Only check payments can include a check #',
      },
    };
  }

  return { transaction };
};

const validateOnlyOneReimbursableMeal = (
  transaction,
  dispatch,
  state,
  { profile }
) => {
  profile = profile || getProfile(state);
  if (!isGenericAccount(profile) && hasMultipleReimbursableMeals(transaction)) {
    return {
      transaction,
      error: {
        type: 'toast',
        title: 'Error',
        message:
          'Student may only purchase one reimbursable meal per Meal Type',
      },
    };
  }

  return { transaction };
};

const transactionValidators = [
  {
    name: 'Valid profile',
    transactionTypes: [
      TransactionTypeEnum.Charge,
      TransactionTypeEnum.Check,
      TransactionTypeEnum.Credit,
      TransactionTypeEnum.Cash,
    ],
    run: validateProfile,
  },
  {
    name: 'Valid transaction object',
    transactionTypes: [
      TransactionTypeEnum.Charge,
      TransactionTypeEnum.Check,
      TransactionTypeEnum.Credit,
      TransactionTypeEnum.Cash,
    ],
    run: validateTransactionObject,
  },
  {
    name: 'Generic account not allowed',
    transactionTypes: [TransactionTypeEnum.Charge, TransactionTypeEnum.Credit],
    run: validateNotGenericAccount,
  },
  {
    name: 'Can charge account',
    transactionTypes: [TransactionTypeEnum.Charge, TransactionTypeEnum.Credit],
    run: validateCanChargeAccount,
  },
  {
    name: 'Use account button for partial payments',
    transactionTypes: [TransactionTypeEnum.Cash],
    run: validateAmountPaid,
  },
  {
    name: 'Has bought items',
    transactionTypes: [TransactionTypeEnum.Cash],
    run: validateHasBoughtItems,
  },
  {
    name: 'Has check number but amount paid is zero',
    transactionTypes: [TransactionTypeEnum.Charge],
    run: validateCheckAmountOrAmountPaid,
  },
  {
    name: 'Amount paid or item selected',
    transactionTypes: [TransactionTypeEnum.Credit, TransactionTypeEnum.Charge],
    run: validateAmountPaidOrItemSelected,
  },
  {
    name: 'Has check number',
    transactionTypes: [TransactionTypeEnum.Check],
    run: validateIsCheckNumber,
  },
  {
    name: 'Has check amount',
    transactionTypes: [TransactionTypeEnum.Check],
    run: validateIsCheckAmount,
  },
  {
    name: 'Is charged meal limit reached',
    transactionTypes: [TransactionTypeEnum.Charge],
    run: validateIsChargedMealLimitReached,
  },
  {
    name: 'Generic account paid with check must match total amount',
    transactionTypes: [TransactionTypeEnum.Check],
    run: validateGenericAccountPaidByCheck,
  },
  {
    name: 'Only check payments can include a check #',
    transactionTypes: [
      TransactionTypeEnum.Cash,
      TransactionTypeEnum.Charge,
      TransactionTypeEnum.Credit,
    ],
    run: validateOnlyCheckPaymentsCanIncludeCheckNumber,
  },
  {
    name: 'Has only one reimbursable meal in transaction',
    transactionTypes: [
      TransactionTypeEnum.Cash,
      TransactionTypeEnum.Charge,
      TransactionTypeEnum.Credit,
      TransactionTypeEnum.Check,
    ],
    run: validateOnlyOneReimbursableMeal,
  },
];

export default (
    transaction,
    data = {},
    { returnOnFirstError = true, displayErrorMessages = true } = {}
  ) =>
  async (dispatch, getState, MPWWebAPI) => {
    transaction = cloneDeep(transaction);
    const state = getState();
    let errors = [];

    const hasTransactionType = (t) => t === transaction.TransactionType;

    // Loop through all of the transactionValidators, if an error is returned from a validator display the message and end the process returning the error.
    // Passing options can prevent the messages from being displayed, and prevent the process stopping on the first error.
    for (const index in transactionValidators) {
      const validator = transactionValidators[index];

      if (validator.transactionTypes.some(hasTransactionType)) {
        const result = await validator.run(transaction, dispatch, state, data);
        transaction = result.transaction;

        if (result.error) {
          errors = [...errors, result.error];
        }

        if (returnOnFirstError && errors.length) {
          break;
        }
      }
    }

    if (errors.length && displayErrorMessages) {
      // If validation errors were found we should dispatch them to the dialog or toast manager.
      errors.forEach((e) => {
        const { type, title, message } = e;
        if (type === 'toast') {
          createToast(toastType.error, title, message);
        }

        if (type === 'dialog') {
          dispatch(setNotificationDialog({ title, message }));
        }
      });
    }

    return { transaction, errors };
  };



// WEBPACK FOOTER //
// ./src/pages/pointofsale/state/transactionValidator.js